import { MySwal, Swal, getClassNameByType, fireCustomButtons } from './Helpers';

// Simple confirm / cancel modal with a plain text body.
export const handleConfirmText = (
  title,
  text,
  type,
  buttons = { confirm: 'Yes', cancel: 'No' },
  options = {},
  confirmCallback = () => false,
  cancelCallback = () => false
) =>
  MySwal.fire({
    title,
    text,
    icon: type,
    showCancelButton: true,
    confirmButtonText: buttons.confirm,
    cancelButtonText: buttons.cancel,
    customClass: {
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-outline-danger ms-1'
    },
    buttonsStyling: false,
    ...options
  }).then((result) => {
    if (result.isConfirmed) confirmCallback();
    else cancelCallback();
  });

// Confirm modal that can render a custom React/HTML body (`component`).
export const handleConfirmAlert = (opt, component = null) => {
  const options = {
    title: undefined,
    text: undefined,
    type: undefined,
    confirmButton: 'btn btn-primary',
    cancelButton: 'btn btn-outline-danger ms-1',
    html: component ?? null,
    buttons: { confirm: 'Yes', cancel: 'No' },
    options: {},
    confirmCallback: () => false,
    cancelCallback: () => false,
    ...opt
  };

  if (typeof options.type === 'undefined') return false;

  return MySwal.fire({
    title: options.title,
    text: options.text,
    icon: options.type,
    html: options.html,
    showCancelButton: true,
    confirmButtonText: options.buttons.confirm,
    cancelButtonText: options.buttons.cancel,
    customClass: {
      confirmButton: options.confirmButton,
      cancelButton: options.cancelButton
    },
    buttonsStyling: false
  }).then((result) => {
    if (result.isConfirmed) options.confirmCallback?.();
    else options.cancelCallback?.();
  });
};

/**
 * Confirmation modal — object API (preferred):
 *   handleConfirmCancel({
 *     title, text, type,
 *     confirmCallback, cancelCallback,
 *     buttons: { confirm, cancel },
 *     options: { confirmClassNames, cancelClassNames },
 *     onClose
 *   })
 * Custom multi-button API (buttons array):
 *   handleConfirmCancel({ title, text, type, buttons: [{ text, color, onClick }], onClose, allowOutsideClick })
 * Legacy positional API is still supported for backward compatibility.
 */
const fireConfirmCancel = ({
  title,
  text = '',
  type = 'warning',
  confirmCallback = () => false,
  cancelCallback = () => false,
  buttons = {},
  options = { confirmClassNames: '', cancelClassNames: '' },
  textClassName,
  onClose
}) => {
  const selectedClass = textClassName || getClassNameByType(type);
  const buttonsTypes = { confirm: 'Yes', cancel: 'No', ...buttons };

  return MySwal.fire({
    title: `<span class="${selectedClass} fs-3">${title}</span>`,
    html: `<span class="fs-6 fw-bold text-secondary">${text}</span>`,
    icon: type ? type : false,
    showCancelButton: !!buttonsTypes.cancel,
    showConfirmButton: !!buttonsTypes.confirm,
    confirmButtonText: buttonsTypes.confirm,
    cancelButtonText: buttonsTypes.cancel,
    customClass: {
      confirmButton: `btn btn-outline-secondary ${options?.confirmClassNames}`,
      cancelButton: `btn btn-outline-secondary ms-1 ${options?.cancelClassNames}`
    },
    buttonsStyling: false
  }).then((result) => {
    if (result && result.value) {
      confirmCallback();
    } else if (result.dismiss === Swal.DismissReason.cancel && typeof cancelCallback === 'function') {
      cancelCallback();
    } else if (result.dismiss && typeof onClose === 'function') {
      onClose();
    }
  });
};

export const handleConfirmCancel = (...args) => {
  if (args[0] && typeof args[0] === 'object') {
    const opt = args[0];
    if (Array.isArray(opt.buttons)) return fireCustomButtons(opt);
    return fireConfirmCancel(opt);
  }

  const [
    title,
    text = '',
    type = 'warning',
    confirmCallback = () => false,
    cancelCallback = () => false,
    buttons = {},
    options = { confirmClassNames: '', cancelClassNames: '' },
    textClassName
  ] = args;

  return fireConfirmCancel({
    title,
    text,
    type,
    confirmCallback,
    cancelCallback,
    buttons,
    options,
    textClassName
  });
};

// Password-gated confirm: proceeds only when the typed value matches `correctText`.
export const handleConfirmInput = (
  title = 'Enter Text',
  correctText,
  confirmCallback = () => {},
  cancelCallback = () => {},
  options = {}
) =>
  MySwal.fire({
    title,
    input: 'password',
    inputPlaceholder: title,
    inputAttributes: { autocapitalize: 'off', autocorrect: 'off', spellcheck: 'false' },
    showCancelButton: true,
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
    showLoaderOnConfirm: true,
    preConfirm: (inputText) => {
      if (inputText === correctText) return true;
      Swal.showValidationMessage('Incorrect Access Code');
      return false;
    },
    allowOutsideClick: () => !Swal.isLoading(),
    customClass: {
      popup: 'swal2-custom-popup',
      confirmButton: 'btn btn-success',
      cancelButton: 'btn btn-outline-danger ms-1',
      input: 'form-control'
    },
    buttonsStyling: false,
    ...options
  }).then((result) => {
    if (result?.isConfirmed) confirmCallback(result?.value);
    else cancelCallback();
  });
